/**
 * Config Service
 *
 * Fetches application configuration from backend
 * Ensures frontend uses same values as backend (no hardcode)
 *
 * @module ConfigService
 * @description Centralized configuration fetching from backend.
 * This service eliminates hardcoded values in frontend by getting all
 * configurable values from /api/config endpoint.
 *
 * Usage:
 * - Import useConfig() hook in components
 * - Access config.referral.usdtRates, config.tokenomics.totalSupply, etc.
 *
 * @see ConfigContext.tsx - React context provider
 * @see backend/src/controllers/config.controller.ts - API endpoint
 */

import { API_CONFIG } from '../config/app.config'

const API_URL = API_CONFIG.baseUrl;

/**
 * Referral rates by level for CASCADE v3.0
 */
export interface ReferralRates {
  [level: number]: number;
}

/**
 * Application configuration interface
 * Must match backend config.controller.ts response
 */
export interface AppConfig {
  vault: {
    activationDelayMinutes: number;
    activationDelayHours: number;
    miningThreshold: number;
    minSolForNftMint: number;
  };
  investment: {
    activationDelayHours: number;
    minAmount: number;
    maxAmount: number;
  };
  marketplace: {
    platformFeePercent: number;
    minListingPrice: number;
    maxListingDays: number;
    disableMarketplaceSale: boolean;
  };
  /** CASCADE v3.0 referral configuration */
  referral: {
    usdtRates: ReferralRates;    // L1: 10%, L2: 6%, L3: 3%
    takaraRates: ReferralRates;  // L1: 10%, L2: 6%, L3: 3%
    treasuryTaxPercent: number;  // 5% tax on TAKARA claims
    instantSaleDiscount?: number; // 20% discount for instant sale
    disableInstantSale: boolean;
    disableClaimTakara: boolean;
    claimMaintenance: boolean;
  };
  /** Tokenomics configuration */
  tokenomics: {
    totalSupply: number;              // 21M TAKARA total
    bitcoinVaultUnlockPercent: number; // 10% mining progress unlocks Bitcoin vault
  };
}

// Default values (used before API response, should match backend defaults)
export const DEFAULT_CONFIG: AppConfig = {
  vault: {
    activationDelayMinutes: 5760, // 72 hours
    activationDelayHours: 72,
    miningThreshold: 25000,
    minSolForNftMint: 0.01
  },
  investment: {
    activationDelayHours: 72,
    minAmount: 100,
    maxAmount: 1000000
  },
  marketplace: {
    platformFeePercent: 2.5,
    minListingPrice: 10,
    maxListingDays: 30,
    disableMarketplaceSale: false
  },
  referral: {
    usdtRates: { 1: 0.10, 2: 0.06, 3: 0.03 },   // CASCADE v3.0
    takaraRates: { 1: 0.10, 2: 0.06, 3: 0.03 }, // CASCADE v3.0
    treasuryTaxPercent: 5,
    instantSaleDiscount: 20,  // 20% discount for instant sale
    disableInstantSale: false,
    disableClaimTakara: false,
    claimMaintenance: false
  },
  tokenomics: {
    totalSupply: 21000000,
    bitcoinVaultUnlockPercent: 10
  }
};

let cachedConfig: AppConfig | null = null;

export async function fetchAppConfig(): Promise<AppConfig> {
  if (cachedConfig) {
    console.debug('[Config] Using cached config:', {
      activationDelayMinutes: cachedConfig.vault.activationDelayMinutes,
      activationDelayHours: cachedConfig.vault.activationDelayHours
    });
    return cachedConfig;
  }

  try {
    console.debug('[Config] Fetching from API...');
    const response = await fetch(`${API_URL}/config`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    });
    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status}`);
    }
    const data = await response.json();
    if (data.success && data.data) {
      cachedConfig = data.data;
      console.info('[Config] Loaded from API:', {
        activationDelayMinutes: cachedConfig.vault.activationDelayMinutes,
        activationDelayHours: cachedConfig.vault.activationDelayHours
      });
      return cachedConfig;
    }
    throw new Error('Invalid config response');
  } catch (error) {
    console.warn('[Config] Failed to fetch, using defaults:', error);
    return DEFAULT_CONFIG;
  }
}

// Clear cache (for testing or refresh)
export function clearConfigCache(): void {
  cachedConfig = null;
}
